import { useState, useEffect, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, Copy, Save, KeyRound, Mail, ShieldAlert, Trash2, Ban, FlaskConical, Gift, Inbox, CheckCircle2, XCircle, AlertCircle, RefreshCw, Calendar, Edit, MessageSquare } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { Checkbox } from '@/components/ui/checkbox';
import AdminEventEditModal from '@/components/AdminEventEditModal';
import { formatEventDate, toLocalDateISO } from '@/lib/formatEventDate';
import { PremiumInstructorBadge } from '@/components/PremiumInstructorBadge';

// Only the fields we expose as editable. Mirrors the backend allow-list
// so a typo in either place fails loudly.
const EDITABLE_KEYS = [
  'first_name', 'last_name', 'display_name', 'email', 'phone',
  'gender', 'bio', 'skill_level', 'has_mahjong_set', 'meetup_type',
  'gender_preference', 'travel_distance', 'zip_code',
  'is_instructor', 'instructor_public', 'instructor_bio', 'instructor_rate',
  'instructor_lesson_types',
  'social_facebook', 'social_instagram', 'social_twitter', 'social_linkedin',
  'hide_from_search', 'allow_messages_from_anyone', 'last_minute_alerts',
  'role', 'subscription_tier', 'subscription_status',
  'subscription_end_date', 'is_banned', 'is_test_account',
  'email_excluded',
  'referral_code',
  // Admin-toggleable flags whose checkboxes were silently un-seeding
  // on page reload because they weren't in this allowlist — the form
  // state never picked up the stored value, so even a checked +
  // saved-true field rendered as unchecked on the next page load.
  // Reported 14 Jun 2026 for SFP; backfilling the rest at the same
  // time. The save path was fine — only the *redisplay* was wrong.
  'is_summer_founding_partner',
  'verified_charity_organizer',
  'sms_consent',
  'newsletter_opt_out',
  'event_alerts_enabled',
  'event_alerts_distance',
  'event_alerts_types',
];

const TIER_OPTS = [
  { value: 'open_play', label: 'Open Play (free, player path)' },
  { value: 'verified_player', label: 'Verified Player ($0.99/mo · $9.99/yr)' },
  { value: 'instructor_starter', label: 'Instructor Starter (free, instructor path)' },
  { value: 'instructor_plus', label: 'Instructor Advantage ($9.99/mo · $99.99/yr)' },
  { value: 'instructor_unlimited', label: 'Instructor Unlimited ($29.99/mo · $299.99/yr)' },
];
const LEGACY_TIERS = new Set(['free', 'monthly', 'annual', 'lifetime']);
const SUB_STATUS_OPTS = ['active', 'cancelled', 'expired', 'none'];
const SKILL_OPTS = ['', 'beginner', 'intermediate', 'advanced', 'expert'];
const SET_OPTS = ['', 'yes', 'no', 'no_preference'];
const MEETUP_OPTS = ['', 'in_person', 'online', 'both'];

const TIER_DISPLAY = {
  open_play: 'Open Play',
  verified_player: 'Verified Player',
  instructor_starter: 'Instructor Starter',
  instructor_plus: 'Instructor Advantage',
  instructor_unlimited: 'Instructor Unlimited',
  free: 'Free (legacy)',
  monthly: 'Monthly (legacy)',
  annual: 'Annual (legacy)',
  lifetime: 'Lifetime (legacy)',
};

function FieldRow({ label, children, hint }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs font-semibold uppercase tracking-wide text-slate-600">{label}</Label>
      {children}
      {hint && <p className="text-xs text-slate-400">{hint}</p>}
    </div>
  );
}

export default function AdminUserProfilePage() {
  const { userId } = useParams();
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [form, setForm] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [pwOpen, setPwOpen] = useState(false);
  const [newPw, setNewPw] = useState('');
  const [emailUser, setEmailUser] = useState(true);
  const [resetting, setResetting] = useState(false);
  const [emailLog, setEmailLog] = useState(null);
  const [emailLogLoading, setEmailLogLoading] = useState(false);
  // Force-cancel PayPal subscription flow — admin rescue button for
  // users where PayPal-side cancellation didn't sync to our DB.
  const [forceCancelling, setForceCancelling] = useState(false);
  const forceCancelSubscription = async () => {
    if (!user?.paypal_subscription_id) return;
    if (!window.confirm(
      `Force-cancel ${user.email}'s subscription at PayPal AND downgrade them to the free tier?\n\n` +
      `This calls PayPal's Cancel Subscription API + updates our DB. It does NOT issue a refund — you'll need to do that manually in the PayPal Business dashboard if needed.\n\n` +
      `PayPal Sub ID: ${user.paypal_subscription_id}`
    )) return;
    setForceCancelling(true);
    try {
      const { data } = await api.post(`/admin/users/${user.user_id || user.id}/force-cancel-subscription`);
      if (data.paypal_call_error) {
        toast.warning(`DB updated to ${data.new_tier}, but PayPal cancel threw: ${data.paypal_call_error}. This is usually fine (already-cancelled subs error) — verify in PayPal.`);
      } else {
        toast.success(`Cancelled at PayPal + downgraded to ${data.new_tier}`);
      }
      // Reload the user record so the UI reflects the change.
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Force-cancel failed. Check the server logs.');
    } finally {
      setForceCancelling(false);
    }
  };
  const [hostedEvents, setHostedEvents] = useState(null);
  const [hostedEventsLoading, setHostedEventsLoading] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [smsLog, setSmsLog] = useState(null);
  const [smsLogLoading, setSmsLogLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/users/${userId}`);
      setUser(data.user);
      const init = {};
      EDITABLE_KEYS.forEach((k) => {
        let v = data.user[k];
        if (k === 'subscription_end_date' && v) {
          // YYYY-MM-DD for <input type="date"> — in the viewer's LOCAL
          // timezone (toISOString() would return the UTC date and dance
          // a day off after UTC midnight).
          v = toLocalDateISO(v);
        }
        init[k] = v ?? (typeof v === 'boolean' ? false : '');
      });
      setForm(init);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load user');
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => { load(); }, [load]);

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleSave = async () => {
    setSaving(true);
    try {
      // Convert blank `subscription_end_date` back to null so the
      // backend clears the field instead of trying to parse "".
      const payload = { ...form };
      if (!payload.subscription_end_date) payload.subscription_end_date = '';
      const { data } = await api.put(`/admin/users/${userId}`, payload);
      toast.success('Profile saved');
      setUser(data.user);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handlePasswordReset = async () => {
    if (newPw.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    setResetting(true);
    try {
      const { data } = await api.post(`/admin/users/${userId}/reset-password`, {
        password: newPw,
        notify_user: emailUser,
      });
      toast.success(
        emailUser && data.notified_user
          ? 'Password reset and emailed to the user'
          : 'Password reset',
      );
      setPwOpen(false);
      setNewPw('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Reset failed');
    } finally {
      setResetting(false);
    }
  };

  const handleBan = async () => {
    try {
      const { data } = await api.post(`/admin/users/${userId}/ban`);
      toast.success(data.message);
      setUser((u) => ({ ...u, is_banned: data.is_banned }));
    } catch { toast.error('Failed to update ban status'); }
  };

  const handleDelete = async () => {
    if (!window.confirm(`Permanently delete ${user.email}? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/users/${userId}`);
      toast.success('User deleted');
      navigate('/admin/users');
    } catch { toast.error('Delete failed'); }
  };

  const copyReferral = () => {
    if (!user?.referral_code) return;
    navigator.clipboard.writeText(user.referral_code);
    toast.success('Referral code copied');
  };

  const loadEmailLog = useCallback(async () => {
    setEmailLogLoading(true);
    try {
      const { data } = await api.get(`/admin/users/${userId}/email-log`);
      setEmailLog(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load email log');
    } finally {
      setEmailLogLoading(false);
    }
  }, [userId]);

  const loadHostedEvents = useCallback(async () => {
    setHostedEventsLoading(true);
    try {
      const { data } = await api.get(`/admin/users/${userId}/events`);
      setHostedEvents(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load events');
    } finally {
      setHostedEventsLoading(false);
    }
  }, [userId]);

  const loadSmsLog = useCallback(async () => {
    setSmsLogLoading(true);
    try {
      const { data } = await api.get(`/admin/users/${userId}/sms-log`);
      setSmsLog(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load SMS log');
    } finally {
      setSmsLogLoading(false);
    }
  }, [userId]);

  // Auto-load events on mount so the admin sees them without an extra click
  // — events are usually a small list (<200) and matter for triage.
  useEffect(() => { loadHostedEvents(); }, [loadHostedEvents]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="animate-spin w-8 h-8 border-4 border-[#b456df] border-t-transparent rounded-full" />
      </div>
    );
  }

  if (!user) {
    return <div className="max-w-3xl mx-auto p-10 text-slate-500">User not found.</div>;
  }

  const fullName = `${user.first_name || ''} ${user.last_name || ''}`.trim() || user.display_name || user.email;

  return (
    <div data-testid="admin-user-profile-page" className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      {/* Header */}
      <div className="flex items-center gap-3 mb-8">
        <Link to="/admin/users" data-testid="admin-back-to-users">
          <Button variant="ghost" size="sm" className="rounded-full"><ArrowLeft className="w-4 h-4" /></Button>
        </Link>
        <div className="flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <h1 className="text-3xl font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>{fullName}</h1>
            {user.is_premium_instructor && (
              <PremiumInstructorBadge size="sm" data-testid="admin-user-premium-badge" />
            )}
          </div>
          <p className="text-slate-500 text-sm mt-1">{user.email}</p>
        </div>
        <div className="flex flex-wrap gap-2">
          {user.is_banned && <Badge variant="destructive" className="rounded-full">Banned</Badge>}
          {user.is_test_account && <Badge className="rounded-full bg-amber-100 text-amber-800">Tester</Badge>}
          {user.role === 'admin' && <Badge className="rounded-full bg-purple-100 text-purple-800">Admin</Badge>}
          <Badge variant="secondary" className="rounded-full">{TIER_DISPLAY[(user.subscription_tier || 'open_play').toLowerCase()] || user.subscription_tier || 'Open Play'}</Badge>
        </div>
      </div>

      {/* At-a-glance */}
      <Card className="border-slate-100 mb-6">
        <CardContent className="p-5 grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
          <div>
            <div className="text-xs text-slate-400 uppercase tracking-wide">Joined</div>
            <div className="font-medium">{user.created_at ? new Date(user.created_at).toLocaleDateString() : '—'}</div>
          </div>
          <div>
            <div className="text-xs text-slate-400 uppercase tracking-wide">Last Active</div>
            <div className="font-medium">{user.last_active_at ? new Date(user.last_active_at).toLocaleDateString() : '—'}</div>
          </div>
          <div>
            <div className="text-xs text-slate-400 uppercase tracking-wide">Subscription Ends</div>
            <div className="font-medium">{user.subscription_end_date ? new Date(user.subscription_end_date).toLocaleDateString() : '—'}</div>
          </div>
          {/* Force-cancel subscription @ PayPal — one-click admin
              rescue for users like Lauren Stemen (27 Jul 2026) who
              cancelled directly in PayPal but the webhook never
              synced, so our DB kept showing 'active' while PayPal
              kept charging her card. Calls
              POST /admin/users/{id}/force-cancel-subscription. */}
          {user.paypal_subscription_id && user.subscription_tier &&
            !['open_play', 'instructor_starter'].includes(user.subscription_tier) && (
            <div className="col-span-2">
              <div className="text-xs text-slate-400 uppercase tracking-wide">PayPal Actions</div>
              <button
                onClick={forceCancelSubscription}
                disabled={forceCancelling}
                className="mt-1 inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full bg-red-50 text-red-700 border border-red-200 hover:bg-red-100 disabled:opacity-50"
                data-testid="admin-force-cancel-btn"
              >
                {forceCancelling ? 'Cancelling…' : 'Force-cancel at PayPal + downgrade'}
              </button>
              <p className="text-[11px] text-slate-400 mt-1">
                Sub ID: <code className="font-mono">{user.paypal_subscription_id}</code>
              </p>
            </div>
          )}
          <div>
            <div className="text-xs text-slate-400 uppercase tracking-wide">Referral Code</div>
            <div className="flex items-center gap-2">
              <code data-testid="admin-user-referral-code" className="font-mono text-sm bg-slate-100 px-2 py-0.5 rounded">
                {user.referral_code || '—'}
              </code>
              {user.referral_code && (
                <button onClick={copyReferral} className="text-slate-400 hover:text-slate-700" data-testid="admin-copy-referral-btn">
                  <Copy className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Email Activity — diagnose "I never got the email" reports */}
      <Card className="border-slate-100 mb-6" data-testid="admin-email-log-card">
        <CardHeader className="flex flex-row items-center justify-between space-y-0">
          <CardTitle className="text-lg flex items-center gap-2">
            <Inbox className="w-4 h-4 text-[#b456df]" /> Email Activity
          </CardTitle>
          <Button
            variant="outline"
            size="sm"
            className="rounded-full gap-1.5"
            onClick={loadEmailLog}
            disabled={emailLogLoading}
            data-testid="admin-email-log-refresh-btn"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${emailLogLoading ? 'animate-spin' : ''}`} />
            {emailLog === null ? 'Load history' : 'Refresh'}
          </Button>
        </CardHeader>
        <CardContent>
          {emailLog === null ? (
            <p className="text-sm text-slate-400">
              Click <strong>Load history</strong> to see the last 25 transactional emails we tried to send to <code className="bg-slate-100 px-1 rounded text-xs">{user.email}</code>.
            </p>
          ) : emailLog.entries.length === 0 ? (
            <p className="text-sm text-slate-500" data-testid="admin-email-log-empty">
              No emails have been logged for this address yet. (Older sends pre-date this log feature.)
            </p>
          ) : (
            <div className="space-y-2" data-testid="admin-email-log-entries">
              {emailLog.entries.map((e) => {
                const cfg =
                  e.status === 'sent'
                    ? { Icon: CheckCircle2, color: 'text-emerald-600', bg: 'bg-emerald-50', border: 'border-emerald-200', label: 'Sent' }
                    : e.status === 'failed'
                    ? { Icon: XCircle, color: 'text-red-600', bg: 'bg-red-50', border: 'border-red-200', label: 'Failed' }
                    : { Icon: AlertCircle, color: 'text-amber-600', bg: 'bg-amber-50', border: 'border-amber-200', label: 'Skipped' };
                return (
                  <div
                    key={e.log_id}
                    className={`flex items-start gap-3 p-3 rounded-xl border ${cfg.border} ${cfg.bg}`}
                    data-testid={`admin-email-log-row-${e.log_id}`}
                  >
                    <cfg.Icon className={`w-4 h-4 mt-0.5 flex-shrink-0 ${cfg.color}`} />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2">
                        <p className="text-sm font-medium text-slate-800 truncate">{e.subject || '(no subject)'}</p>
                        <span className={`text-[11px] font-semibold uppercase tracking-wider ${cfg.color}`}>{cfg.label}</span>
                      </div>
                      <p className="text-xs text-slate-500 mt-0.5">
                        {e.created_at ? new Date(e.created_at).toLocaleString() : '—'}
                        {e.resend_id && (
                          <>
                            {' · '}
                            <span className="font-mono text-[11px] text-slate-400">{e.resend_id.slice(0, 8)}…</span>
                          </>
                        )}
                      </p>
                      {e.error && (
                        <p className="text-xs text-red-600 mt-1 font-mono break-words">{e.error}</p>
                      )}
                    </div>
                  </div>
                );
              })}
              <p className="text-[11px] text-slate-400 pt-1">
                &quot;Sent&quot; means Resend accepted the email — it doesn&apos;t guarantee delivery to the inbox. Check Resend&apos;s dashboard for bounce / suppression status.
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* SMS Inbound Log — see when the user texted STOP, HELP, START, or
          anything else. Helps disambiguate "admin flipped consent" vs
          "user opted out themselves" when investigating complaints. */}
      <Card className="border-slate-100 mb-6" data-testid="admin-sms-log-card">
        <CardHeader className="flex flex-row items-center justify-between space-y-0">
          <CardTitle className="text-lg flex items-center gap-2">
            <MessageSquare className="w-4 h-4 text-[#df5681]" /> Inbound SMS
            {smsLog && smsLog.phone && (
              <span className="text-[11px] font-normal text-slate-400 ml-2 font-mono">{smsLog.phone}</span>
            )}
          </CardTitle>
          <Button
            variant="outline"
            size="sm"
            className="rounded-full gap-1.5"
            onClick={loadSmsLog}
            disabled={smsLogLoading}
            data-testid="admin-sms-log-refresh-btn"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${smsLogLoading ? 'animate-spin' : ''}`} />
            {smsLog === null ? 'Load history' : 'Refresh'}
          </Button>
        </CardHeader>
        <CardContent>
          {smsLog === null ? (
            <p className="text-sm text-slate-400">
              Click <strong>Load history</strong> to see texts this user has sent to our Twilio number (STOP, HELP, etc.).
            </p>
          ) : !smsLog.phone ? (
            <p className="text-sm text-slate-500">This user has no phone number on file.</p>
          ) : smsLog.entries.length === 0 ? (
            <p className="text-sm text-slate-500" data-testid="admin-sms-log-empty">
              No inbound SMS from this number yet.
            </p>
          ) : (
            <div className="space-y-2" data-testid="admin-sms-log-entries">
              {smsLog.entries.map((e, i) => {
                const cfg =
                  e.action === 'opt_out'
                    ? { color: 'text-red-700', bg: 'bg-red-50', border: 'border-red-200' }
                    : e.action === 'opt_in'
                    ? { color: 'text-emerald-700', bg: 'bg-emerald-50', border: 'border-emerald-200' }
                    : e.action === 'help'
                    ? { color: 'text-blue-700', bg: 'bg-blue-50', border: 'border-blue-200' }
                    : { color: 'text-slate-700', bg: 'bg-slate-50', border: 'border-slate-200' };
                return (
                  <div key={i} className={`flex items-start gap-3 p-3 rounded-xl border ${cfg.border} ${cfg.bg}`}>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2">
                        <p className="text-sm font-medium text-slate-800">
                          {e.keyword || '(no keyword)'}
                          <span className={`text-[11px] font-semibold uppercase tracking-wider ml-2 ${cfg.color}`}>{e.action}</span>
                        </p>
                        <span className="text-[11px] text-slate-400 flex-shrink-0">
                          {e.received_at ? new Date(e.received_at).toLocaleString() : ''}
                        </span>
                      </div>
                      <p className="text-xs text-slate-500 font-mono mt-0.5 truncate">{e.body || '—'}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>


      {/* Hosted Events — every event this user has posted (live, draft, canceled).
          Click any row to open the same Admin Event Edit Modal used on the
          /admin/events table. */}
      <Card className="border-slate-100 mb-6" data-testid="admin-user-events-card">
        <CardHeader className="flex flex-row items-center justify-between space-y-0">
          <CardTitle className="text-lg flex items-center gap-2">
            <Calendar className="w-4 h-4 text-[#56dfb4]" /> Hosted Events
            {hostedEvents !== null && (
              <span className="text-[11px] font-normal text-slate-400 ml-2">{hostedEvents.count}</span>
            )}
          </CardTitle>
          <Button
            variant="outline"
            size="sm"
            className="rounded-full gap-1.5"
            onClick={loadHostedEvents}
            disabled={hostedEventsLoading}
            data-testid="admin-user-events-refresh-btn"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${hostedEventsLoading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </CardHeader>
        <CardContent>
          {hostedEvents === null && !hostedEventsLoading ? (
            <p className="text-sm text-slate-400">Loading events…</p>
          ) : hostedEvents && hostedEvents.events.length === 0 ? (
            <p className="text-sm text-slate-500" data-testid="admin-user-events-empty">
              This user hasn&apos;t hosted any events yet.
            </p>
          ) : (
            <div className="space-y-2" data-testid="admin-user-events-list">
              {(hostedEvents?.events || []).map((e) => {
                const statusCfg =
                  e.status === 'canceled'
                    ? { color: 'text-red-700', bg: 'bg-red-50', border: 'border-red-200', label: 'Canceled' }
                    : e.payment_status === 'draft'
                    ? { color: 'text-amber-800', bg: 'bg-amber-50', border: 'border-amber-200', label: 'Draft' }
                    : e.payment_status === 'published'
                    ? { color: 'text-emerald-700', bg: 'bg-emerald-50', border: 'border-emerald-200', label: 'Live' }
                    : { color: 'text-slate-700', bg: 'bg-slate-50', border: 'border-slate-200', label: e.payment_status || '—' };
                return (
                  <button
                    key={e.event_id}
                    onClick={() => setEditingEvent(e)}
                    className={`w-full text-left flex items-start gap-3 p-3 rounded-xl border ${statusCfg.border} ${statusCfg.bg} hover:shadow-sm transition-all group`}
                    data-testid={`admin-user-event-row-${e.event_id}`}
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2 mb-0.5">
                        <p className="text-sm font-medium text-slate-800 truncate group-hover:underline">{e.title || '(untitled)'}</p>
                        <span className={`text-[11px] font-semibold uppercase tracking-wider ${statusCfg.color} flex-shrink-0`}>{statusCfg.label}</span>
                      </div>
                      <p className="text-xs text-slate-500">
                        {e.event_date ? formatEventDate(e.event_date) : 'TBD'}
                        {e.venue_name ? ` · ${e.venue_name}` : ''}
                        {e.city ? `, ${e.city}` : ''}
                        {e.is_destination_event ? ' · 🌍' : ''}
                      </p>
                      <p className="text-[11px] text-slate-400 mt-0.5">
                        {e.event_type} · {e.is_free === false ? `$${(e.cost_amount || 0).toFixed(2)}` : 'Free'}
                      </p>
                    </div>
                    <Edit className="w-3.5 h-3.5 text-slate-400 group-hover:text-[#b456df] flex-shrink-0 mt-1" />
                  </button>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>



      {/* Account section */}
      <Card className="border-slate-100 mb-6">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Mail className="w-4 h-4 text-[#b456df]" /> Account
          </CardTitle>
        </CardHeader>
        <CardContent className="grid md:grid-cols-2 gap-4">
          <FieldRow label="First Name">
            <Input data-testid="admin-edit-first-name" value={form.first_name || ''} onChange={(e) => setField('first_name', e.target.value)} />
          </FieldRow>
          <FieldRow label="Last Name">
            <Input data-testid="admin-edit-last-name" value={form.last_name || ''} onChange={(e) => setField('last_name', e.target.value)} />
          </FieldRow>
          <FieldRow label="Display Name">
            <Input data-testid="admin-edit-display-name" value={form.display_name || ''} onChange={(e) => setField('display_name', e.target.value)} />
          </FieldRow>
          <FieldRow label="Email" hint="Used for login. Lowercased on save; must be unique.">
            <Input data-testid="admin-edit-email" type="email" value={form.email || ''} onChange={(e) => setField('email', e.target.value)} />
          </FieldRow>
          <FieldRow label="Phone">
            <Input data-testid="admin-edit-phone" value={form.phone || ''} onChange={(e) => setField('phone', e.target.value)} />
          </FieldRow>
          <FieldRow label="Gender">
            <Input data-testid="admin-edit-gender" value={form.gender || ''} onChange={(e) => setField('gender', e.target.value)} />
          </FieldRow>
          <FieldRow label="Zip Code">
            <Input data-testid="admin-edit-zip" value={form.zip_code || ''} onChange={(e) => setField('zip_code', e.target.value)} />
          </FieldRow>
          <FieldRow label="Travel Distance (miles)">
            <Input data-testid="admin-edit-travel-distance" value={form.travel_distance || ''} onChange={(e) => setField('travel_distance', e.target.value)} />
          </FieldRow>
          <div className="md:col-span-2">
            <FieldRow label="Bio">
              <Textarea data-testid="admin-edit-bio" rows={3} value={form.bio || ''} onChange={(e) => setField('bio', e.target.value)} />
            </FieldRow>
          </div>
        </CardContent>
      </Card>

      {/* Mahjong preferences */}
      <Card className="border-slate-100 mb-6">
        <CardHeader><CardTitle className="text-lg">Mahjong Preferences</CardTitle></CardHeader>
        <CardContent className="grid md:grid-cols-2 gap-4">
          <FieldRow label="Skill Level">
            <select className="w-full border rounded-md px-3 py-2 text-sm" value={form.skill_level || ''} onChange={(e) => setField('skill_level', e.target.value)} data-testid="admin-edit-skill-level">
              {SKILL_OPTS.map((o) => <option key={o} value={o}>{o || '—'}</option>)}
            </select>
          </FieldRow>
          <FieldRow label="Has Mahjong Set">
            <select className="w-full border rounded-md px-3 py-2 text-sm" value={form.has_mahjong_set || ''} onChange={(e) => setField('has_mahjong_set', e.target.value)} data-testid="admin-edit-has-set">
              {SET_OPTS.map((o) => <option key={o} value={o}>{o || '—'}</option>)}
            </select>
          </FieldRow>
          <FieldRow label="Meetup Type">
            <select className="w-full border rounded-md px-3 py-2 text-sm" value={form.meetup_type || ''} onChange={(e) => setField('meetup_type', e.target.value)} data-testid="admin-edit-meetup-type">
              {MEETUP_OPTS.map((o) => <option key={o} value={o}>{o || '—'}</option>)}
            </select>
          </FieldRow>
          <FieldRow label="Gender Preference">
            <Input value={form.gender_preference || ''} onChange={(e) => setField('gender_preference', e.target.value)} data-testid="admin-edit-gender-pref" />
          </FieldRow>
        </CardContent>
      </Card>

      {/* Instructor */}
      <Card className="border-slate-100 mb-6">
        <CardHeader><CardTitle className="text-lg">Instructor</CardTitle></CardHeader>
        <CardContent className="grid md:grid-cols-2 gap-4">
          <FieldRow label="Is Instructor">
            <div className="flex items-center gap-2 mt-2"><Checkbox checked={!!form.is_instructor} onCheckedChange={(v) => setField('is_instructor', !!v)} data-testid="admin-edit-is-instructor" /><span className="text-sm">Yes</span></div>
          </FieldRow>
          <FieldRow label="Public Instructor Listing">
            <div className="flex items-center gap-2 mt-2"><Checkbox checked={!!form.instructor_public} onCheckedChange={(v) => setField('instructor_public', !!v)} data-testid="admin-edit-instructor-public" /><span className="text-sm">Yes</span></div>
          </FieldRow>
          <FieldRow label="Instructor Rate">
            <Input value={form.instructor_rate || ''} onChange={(e) => setField('instructor_rate', e.target.value)} placeholder="$40/hr" data-testid="admin-edit-instructor-rate" />
          </FieldRow>
          <div className="md:col-span-2">
            <FieldRow label="Instructor Bio">
              <Textarea rows={3} value={form.instructor_bio || ''} onChange={(e) => setField('instructor_bio', e.target.value)} data-testid="admin-edit-instructor-bio" />
            </FieldRow>
          </div>
        </CardContent>
      </Card>

      {/* Social */}
      <Card className="border-slate-100 mb-6">
        <CardHeader><CardTitle className="text-lg">Social Links</CardTitle></CardHeader>
        <CardContent className="grid md:grid-cols-2 gap-4">
          {['social_facebook', 'social_instagram', 'social_twitter', 'social_linkedin'].map((k) => (
            <FieldRow key={k} label={k.replace('social_', '')}>
              <Input value={form[k] || ''} onChange={(e) => setField(k, e.target.value)} data-testid={`admin-edit-${k.replace('_', '-')}`} />
            </FieldRow>
          ))}
        </CardContent>
      </Card>

      {/* Admin controls */}
      <Card className="border-slate-100 mb-6">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2"><ShieldAlert className="w-4 h-4 text-[#df5681]" /> Admin Controls</CardTitle>
        </CardHeader>
        <CardContent className="grid md:grid-cols-2 gap-4">
          <FieldRow label="Role">
            <select className="w-full border rounded-md px-3 py-2 text-sm" value={form.role || 'user'} onChange={(e) => setField('role', e.target.value)} data-testid="admin-edit-role">
              <option value="user">user</option>
              <option value="admin">admin</option>
            </select>
          </FieldRow>
          <FieldRow label="Referral Code" hint="Unique. Uppercased on save.">
            <Input value={form.referral_code || ''} onChange={(e) => setField('referral_code', e.target.value)} data-testid="admin-edit-referral-code" />
          </FieldRow>
          <FieldRow
            label="Subscription Tier"
            hint={LEGACY_TIERS.has((form.subscription_tier || '').toLowerCase())
              ? `Currently on legacy tier "${form.subscription_tier}". Selecting a new tier will migrate this user when you Save.`
              : undefined}
          >
            <select className="w-full border rounded-md px-3 py-2 text-sm" value={form.subscription_tier || 'open_play'} onChange={(e) => setField('subscription_tier', e.target.value)} data-testid="admin-edit-tier">
              {/* Preserve legacy value as a non-default option so admins
                  can see it before migrating; the badge in the hint
                  flags it as legacy. */}
              {LEGACY_TIERS.has((form.subscription_tier || '').toLowerCase()) && (
                <option value={form.subscription_tier} disabled>
                  ⚠ legacy: {form.subscription_tier}
                </option>
              )}
              {TIER_OPTS.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
          </FieldRow>
          <FieldRow label="Subscription Status">
            <select className="w-full border rounded-md px-3 py-2 text-sm" value={form.subscription_status || ''} onChange={(e) => setField('subscription_status', e.target.value)} data-testid="admin-edit-sub-status">
              <option value="">—</option>
              {SUB_STATUS_OPTS.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </FieldRow>
          <FieldRow label="Subscription End Date">
            <Input type="date" value={form.subscription_end_date || ''} onChange={(e) => setField('subscription_end_date', e.target.value)} data-testid="admin-edit-sub-end-date" />
          </FieldRow>
          <FieldRow label="Flags">
            <div className="flex flex-col gap-2 mt-2">
              <label className="flex items-center gap-2 text-sm"><Checkbox checked={!!form.is_banned} onCheckedChange={(v) => setField('is_banned', !!v)} data-testid="admin-edit-is-banned" /> Banned</label>
              <label className="flex items-center gap-2 text-sm"><Checkbox checked={!!form.is_test_account} onCheckedChange={(v) => setField('is_test_account', !!v)} data-testid="admin-edit-is-test-account" /> Test Account (hidden from search)</label>
              <label className="flex items-center gap-2 text-sm"><Checkbox checked={!!form.email_excluded} onCheckedChange={(v) => setField('email_excluded', !!v)} data-testid="admin-edit-email-excluded" /> Remove from email list <span className="text-xs text-slate-400">(blanks email column in CSV exports and blocks all bulk emails)</span></label>
              <label className="flex items-center gap-2 text-sm"><Checkbox checked={!!form.verified_charity_organizer} onCheckedChange={(v) => setField('verified_charity_organizer', !!v)} data-testid="admin-edit-verified-charity-organizer" /> Verified Charity Organizer <span className="text-xs text-slate-400">(unlimited free charity event postings; auto-approved)</span></label>
              <label className="flex items-center gap-2 text-sm"><Checkbox checked={!!form.is_summer_founding_partner} onCheckedChange={(v) => setField('is_summer_founding_partner', !!v)} data-testid="admin-edit-summer-founding-partner" /> Summer Founding Partner <span className="text-xs text-slate-400">(sun badge on profile + unlimited free event posting for events on/before Labor Day 2026)</span></label>
              <label className="flex items-center gap-2 text-sm"><Checkbox checked={!!form.hide_from_search} onCheckedChange={(v) => setField('hide_from_search', !!v)} /> Hide from player search</label>
              <label className="flex items-center gap-2 text-sm"><Checkbox checked={!!form.allow_messages_from_anyone} onCheckedChange={(v) => setField('allow_messages_from_anyone', !!v)} /> Allow messages from anyone</label>
              <label className="flex items-center gap-2 text-sm"><Checkbox checked={!!form.last_minute_alerts} onCheckedChange={(v) => setField('last_minute_alerts', !!v)} /> Last-minute alerts</label>
            </div>
          </FieldRow>
        </CardContent>
      </Card>

      {/* Save + Actions */}
      <div className="sticky bottom-4 bg-white/90 backdrop-blur border border-slate-200 rounded-2xl shadow-lg p-4 flex flex-wrap items-center gap-3 justify-between">
        <div className="flex flex-wrap gap-2">
          <Button
            onClick={() => setPwOpen(true)}
            variant="outline"
            className="rounded-full gap-2"
            data-testid="admin-open-reset-password-btn"
          >
            <KeyRound className="w-4 h-4" /> Reset Password
          </Button>
          <Button
            onClick={handleBan}
            variant="outline"
            className={`rounded-full gap-2 ${user.is_banned ? 'text-green-700 border-green-300' : 'text-amber-700 border-amber-300'}`}
            data-testid="admin-toggle-ban-btn"
          >
            <Ban className="w-4 h-4" /> {user.is_banned ? 'Unban' : 'Ban'}
          </Button>
          <Button
            onClick={handleDelete}
            variant="outline"
            className="rounded-full gap-2 text-red-700 border-red-300"
            data-testid="admin-delete-user-btn"
          >
            <Trash2 className="w-4 h-4" /> Delete
          </Button>
        </div>
        <Button
          onClick={handleSave}
          disabled={saving}
          className="rounded-full gap-2 bg-[#56dfb4] hover:bg-[#56dfb4]/90 text-slate-900"
          data-testid="admin-save-user-btn"
        >
          <Save className="w-4 h-4" /> {saving ? 'Saving…' : 'Save Changes'}
        </Button>
      </div>

      {/* Password reset modal */}
      <Dialog open={pwOpen} onOpenChange={setPwOpen}>
        <DialogContent data-testid="admin-reset-password-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><KeyRound className="w-5 h-5 text-[#b456df]" /> Reset Password</DialogTitle>
            <DialogDescription>
              Set a new password for <strong>{user.email}</strong>. Use this when a user has lost access
              and the self-serve forgot-password link isn't an option.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <FieldRow label="New Password" hint="Minimum 6 characters.">
              <Input
                type="text"
                value={newPw}
                onChange={(e) => setNewPw(e.target.value)}
                placeholder="Type the new password…"
                data-testid="admin-new-password-input"
              />
            </FieldRow>
            <label className="flex items-center gap-2 text-sm text-slate-700">
              <Checkbox checked={emailUser} onCheckedChange={(v) => setEmailUser(!!v)} data-testid="admin-notify-user-checkbox" />
              Email the new password to {user.email}
            </label>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPwOpen(false)} className="rounded-full">Cancel</Button>
            <Button
              onClick={handlePasswordReset}
              disabled={resetting || newPw.length < 6}
              className="rounded-full bg-[#b456df] hover:bg-[#b456df]/90 text-white"
              data-testid="admin-confirm-reset-password-btn"
            >
              {resetting ? 'Resetting…' : 'Reset Password'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Silenced unused-import warnings keep these icons handy if the
          page grows with more admin actions later. */}
      <span className="hidden"><FlaskConical /><Gift /></span>

      {/* Admin event editor — invoked when an admin clicks an event row in
          the Hosted Events card above. Mounts as a modal so the rest of
          the user profile context stays in view. */}
      {editingEvent && (
        <AdminEventEditModal
          event={editingEvent}
          mode="admin"
          onClose={() => setEditingEvent(null)}
          onSaved={(updated) => {
            setHostedEvents((prev) => prev ? {
              ...prev,
              events: prev.events.map((e) => e.event_id === updated.event_id ? { ...e, ...updated } : e),
            } : prev);
            setEditingEvent(null);
          }}
        />
      )}
    </div>
  );
}
